/**
 * Navigation Utilities
 * Provides a way to navigate using React Router from outside React components
 * This is needed for deep linking from notifications
 */
import { Capacitor } from '@capacitor/core';
import { getFeatureFlags } from '../config/featureFlags';

// Global reference to React Router's navigate function
let globalNavigate = null;

/**
 * Set the global navigate function from React Router
 * Should be called once from App.js after Router is initialized
 * @param {Function} navigate - React Router's navigate function
 */
export const setGlobalNavigate = (navigate) => {
  globalNavigate = navigate;
};

/**
 * Navigate using React Router
 * @param {string} path - Path to navigate to
 * @param {Object} options - Navigation options (state, replace, etc.)
 * @returns {boolean} - True if navigation was successful, false otherwise
 */
export const navigate = (path, options = {}) => {
  if (!globalNavigate) {
    window.location.href = path;
    return false;
  }

  try {
    globalNavigate(path, options);
    return true;
  } catch (error) {
    // Fallback to window.location
    window.location.href = path;
    return false;
  }
};

/**
 * Navigate to orders page, optionally with orderId filter
 * @param {string} orderId - Optional order ID to filter/highlight
 */
export const navigateToOrders = (orderId = null) => {
  if (orderId) {
    navigate(`/orders?orderId=${encodeURIComponent(orderId)}`, { replace: false });
  } else {
    navigate('/orders', { replace: false });
  }
};

/**
 * Navigate to profile page
 * @param {string} userId - Optional user ID (currently not used, kept for future compatibility)
 */
export const navigateToProfile = (userId = null) => {
  navigate('/profile', { replace: false });
};

/**
 * Navigate to user management page
 */
export const navigateToUserManagement = () => {
  // User management might not have a direct route, redirect to orders as fallback
  // This will be updated if a user management route is added
  navigate('/orders', { replace: false });
};

/**
 * Navigate to dashboard
 */
export const navigateToDashboard = () => {
  navigate('/dashboard', { replace: false });
};

/**
 * Navigate to account plan page
 */
export const navigateToAccountPlan = () => {
  navigate('/account-plan', { replace: false });
};

export const navigateToReceivables = () => {
  navigate('/receivables', { replace: false });
};

/**
 * Handle notification deep link navigation
 * @param {Object} data - Notification data from notification click
 */
export const handleNotificationDeepLink = (data) => {
  if (!data) {
    navigateToDashboard();
    return;
  }

  try {
    switch (data.type) {
      case 'order_update':
      case 'ORDER_PLACED':
      case 'ORDER_APPROVED':
      case 'ORDER_READY_TO_DISPATCH':
      case 'ORDER_DISPATCHED':
        if (data.orderId) {
          navigateToOrders(data.orderId);
        } else {
          navigateToOrders();
        }
        break;

      case 'PAYMENT_DUE_REMINDER':
        navigateToReceivables();
        break;

      case 'user_notification':
        if (data.userId) {
          navigateToProfile(data.userId);
        } else {
          navigateToProfile();
        }
        break;

      case 'approval':
        if (data.entityType === 'organization') {
          // Organization approval - no specific route, go to dashboard
          navigateToDashboard();
        } else if (data.entityType === 'user') {
          // User approval - navigate to user management or orders
          navigateToUserManagement();
        } else {
          navigateToDashboard();
        }
        break;

      default:
        // Default navigation to dashboard
        navigateToDashboard();
    }
  } catch (error) {
    // Fallback to orders page
    navigateToOrders();
  }
};

/**
 * Validate if a route exists in the application
 * @param {string} path - Path to validate
 * @returns {boolean} - True if route likely exists
 */
export const isValidRoute = (path) => {
  const { inventory: inventoryEnabled, receivables: receivablesEnabled } = getFeatureFlags();
  const includeOrgRegistration = !(Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios');
  const baseRoutes = [
    '/',
    '/landing',
    '/login',
    '/register',
    '/privacy-policy',
    '/terms-of-service',
    '/admin/organization-approval',
    '/pending-approval',
    '/trial-expired',
    '/orders',
    '/new-order',
    '/profile',
    '/account-plan',
    '/dashboard',
  ];
  const publicRoutes = includeOrgRegistration ? [...baseRoutes, '/register-organization'] : baseRoutes;

  const inventoryRoutes = [
    '/inventory',
    '/inventory/products',
    '/inventory/stock-in',
    '/inventory/stock-out',
    '/inventory/transactions',
    '/inventory/reports',
    '/inventory/settings',
  ];

  const receivablesRoutes = [
    '/receivables',
    '/receivables/new',
    '/receivables/edit',
    '/receivables/pay',
  ];

  const validRoutes = [
    ...publicRoutes,
    ...(inventoryEnabled ? inventoryRoutes : []),
    ...(receivablesEnabled ? receivablesRoutes : []),
  ];

  // Check exact match or if it starts with a valid route
  return validRoutes.some(route => path === route || path.startsWith(route + '/'));
};

